import { Box, Paper, Stack, Typography, Chip } from '@mui/material';
import { DataGridPro, GridColDef } from '@mui/x-data-grid-pro';
import React from 'react';

interface IncomingProps {
  theme: 'dark' | 'light';
}

// Mock incoming calls data
const mockIncomingCalls = [
  { 
    id: '1', 
    caller: 'John Smith', 
    phone: '+1 (555) 123-4567',
    timestamp: '2023-06-15 10:30 AM', 
    duration: '3:42',
    status: 'Completed' 
  },
  { 
    id: '2', 
    caller: 'Emily Johnson', 
    phone: '+1 (555) 987-6543',
    timestamp: '2023-06-15 11:15 AM', 
    duration: '2:18',
    status: 'Completed' 
  },
  { 
    id: '3', 
    caller: 'Unknown', 
    phone: '+1 (555) 234-5678',
    timestamp: '2023-06-14 2:45 PM', 
    duration: '0:48',
    status: 'Missed' 
  },
  { 
    id: '4', 
    caller: 'Sarah Williams', 
    phone: '+1 (555) 345-6789',
    timestamp: '2023-06-14 4:20 PM', 
    duration: '5:07',
    status: 'Completed' 
  },
  { 
    id: '5', 
    caller: 'Unknown', 
    phone: '+1 (555) 456-7890',
    timestamp: '2023-06-13 9:10 AM', 
    duration: '0:00',
    status: 'Rejected' 
  },
];

// Mock context data
const mockContextData = {
  customerInfo: "Premium customer since 2020",
  previousInteractions: [
    "Last call on May 5th - Discussed service renewal",
    "Email sent on May 10th - Product upgrade options"
  ],
  recentPurchases: "Enterprise package (March 2023)",
  notes: "Interested in expanding to international markets"
};

const IncomingTab: React.FC<IncomingProps> = ({ theme }) => {
  // Colors based on the platform's theme with more muted, grayish tones
  const colors = {
    paperBg: theme === 'dark' ? '#202020' : '#f9f9f9',
    paperBorder: theme === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
    textPrimary: theme === 'dark' ? '#c8c8c8' : '#38383b',
    textSecondary: theme === 'dark' ? '#a0a0a0' : '#6c6c6c',
    statusChipCompleted: theme === 'dark' ? '#2e7d32' : '#4caf50',
    statusChipMissed: theme === 'dark' ? '#ed6c02' : '#ff9800',
    statusChipRejected: theme === 'dark' ? '#d32f2f' : '#f44336',
  };

  // Define columns for the DataGrid
  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 70 },
    { field: 'caller', headerName: 'Caller', width: 150 },
    { field: 'phone', headerName: 'Phone Number', width: 150 },
    { field: 'timestamp', headerName: 'Date & Time', width: 180 },
    { field: 'duration', headerName: 'Duration', width: 100 },
    { 
      field: 'status', 
      headerName: 'Status', 
      width: 120,
      renderCell: (params) => {
        let chipColor;
        switch (params.value) {
          case 'Completed':
            chipColor = colors.statusChipCompleted;
            break;
          case 'Missed':
            chipColor = colors.statusChipMissed;
            break;
          case 'Rejected':
          default:
            chipColor = colors.statusChipRejected;
            break;
        }
        
        return (
          <Chip 
            label={params.value} 
            size="small" 
            sx={{ 
              backgroundColor: chipColor,
              color: 'white',
              fontWeight: 'bold'
            }} 
          />
        );
      }
    }
  ];

  return (
    <Stack spacing={3}>
      {/* Incoming Calls History */}
      <Paper
        elevation={2}
        sx={{
          height: '100%',
          minHeight: 300,
          borderRadius: 2,
          display: 'flex',
          flexDirection: 'column',
          background: colors.paperBg,
          border: '1px solid',
          borderColor: colors.paperBorder,
          boxShadow: theme === 'dark' 
            ? '0 2px 8px rgba(0, 0, 0, 0.15)' 
            : '0 2px 8px rgba(0, 0, 0, 0.05)',
        }}
      >
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Typography variant="h5" color={colors.textPrimary}>Incoming Calls History</Typography>
        </Box>
        <Box sx={{ flexGrow: 1, p: 2 }}>
          <DataGridPro
            rows={mockIncomingCalls}
            columns={columns}
            showCellVerticalBorder
            hideFooter={mockIncomingCalls.length <= 5}
            sx={{
              backgroundColor: theme === 'dark' ? '#1a1a1a' : '#fff',
              borderColor: colors.paperBorder,
              color: colors.textPrimary,
              '& .MuiDataGrid-cell': {
                borderColor: colors.paperBorder,
              },
              '& .MuiDataGrid-columnHeader': {
                backgroundColor: theme === 'dark' ? '#333' : '#f5f5f5',
                color: colors.textPrimary,
              },
            }}
          />
        </Box>
      </Paper>

      {/* Context Form */}
      <Paper
        elevation={2}
        sx={{
          p: 4,
          minHeight: 200,
          borderRadius: 2,
          display: 'flex',
          flexDirection: 'column',
          background: colors.paperBg,
          border: '1px solid',
          borderColor: colors.paperBorder,
          boxShadow: theme === 'dark' 
            ? '0 2px 8px rgba(0, 0, 0, 0.15)' 
            : '0 2px 8px rgba(0, 0, 0, 0.05)',
        }}
      >
        <Typography variant="h5" sx={{ mb: 3, textAlign: 'center' }} color={colors.textPrimary}>
          Context Form
        </Typography>
        
        <Box sx={{ p: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }} color={colors.textPrimary}>
            Customer Information
          </Typography>
          <Typography variant="body2" sx={{ mb: 3, p: 1, border: '1px solid', borderColor: colors.paperBorder, borderRadius: 1 }} color={colors.textPrimary}>
            {mockContextData.customerInfo}
          </Typography>
          
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }} color={colors.textPrimary}>
            Previous Interactions
          </Typography>
          <Box sx={{ mb: 3, p: 1, border: '1px solid', borderColor: colors.paperBorder, borderRadius: 1 }}>
            {mockContextData.previousInteractions.map((interaction, index) => (
              <Typography key={index} variant="body2" sx={{ mb: 0.5 }} color={colors.textPrimary}>
                • {interaction}
              </Typography>
            ))}
          </Box>
          
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }} color={colors.textPrimary}>
            Recent Purchases
          </Typography>
          <Typography variant="body2" sx={{ mb: 3, p: 1, border: '1px solid', borderColor: colors.paperBorder, borderRadius: 1 }} color={colors.textPrimary}>
            {mockContextData.recentPurchases}
          </Typography>
          
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }} color={colors.textPrimary}>
            Notes
          </Typography>
          <Typography variant="body2" sx={{ p: 1, border: '1px solid', borderColor: colors.paperBorder, borderRadius: 1 }} color={colors.textPrimary}>
            {mockContextData.notes}
          </Typography>
        </Box>
      </Paper>
    </Stack>
  );
};

export default IncomingTab; 